<template>
  <section class="px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-2xl">
      <div class="mb-5 flex min-w-0 items-start gap-3">
        <HistoryBackButton />
        <div class="min-w-0">
          <p class="m-0 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Settings</p>
          <h1 class="m-0 mt-1 text-2xl font-semibold text-slate-950 sm:text-3xl">Timezone</h1>
          <p class="mt-2 text-sm font-medium leading-6 text-slate-500">Update the timezone used for flyer timing and local results.</p>
        </div>
      </div>

      <form class="overflow-hidden rounded-[1.1rem] border border-blue-100 bg-white shadow-sm" @submit.prevent="saveTimezone">
        <div class="grid gap-4 p-5">
          <label class="grid gap-1.5 text-sm font-semibold text-slate-700">
            <span>Timezone</span>
            <select
              v-model="timezone"
              class="min-h-12 rounded-2xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
              :disabled="saving"
            >
              <option v-for="item in timezoneOptions" :key="item" :value="item">
                {{ item }}
              </option>
            </select>
          </label>

          <label class="grid gap-1.5 text-sm font-semibold text-slate-700">
            <span>Zone</span>
            <input
              class="min-h-12 rounded-2xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-900 outline-none"
              type="text"
              :value="zone"
              readonly
            />
          </label>

          <div v-if="message" class="rounded-2xl border px-4 py-3 text-sm font-medium" :class="messageClass">
            {{ message }}
          </div>
        </div>

        <div class="flex justify-end border-t border-blue-100 bg-slate-50 px-5 py-4">
          <button
            class="nf-primary-button"
            type="submit"
            :disabled="saving || !timezone"
          >
            <i class="ri-save-3-line text-base" aria-hidden="true"></i>
            <span>{{ saving ? 'Saving...' : 'Save timezone' }}</span>
          </button>
        </div>
      </form>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useHead } from '@vueuse/head'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'

useHead({
  title: 'Timezone Settings | NetFlyr',
  meta: [
    { name: 'description', content: 'Update your NetFlyr timezone settings so flyer dates, checkins, chats, account activity, and local community updates appear with accurate regional time across your experience.' },
    { name: 'keywords', content: 'NetFlyr timezone, timezone settings, account settings, local time, flyer dates, checkin time, regional settings' }
  ]
})

const FALLBACK_TIMEZONES = [
  'UTC',
  'Asia/Kolkata',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney'
]

const session = useSessionStore()
const timezone = ref(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC')
const saving = ref(false)
const message = ref('')
const messageType = ref('success')

const timezoneOptions = computed(() => {
  const supported = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : FALLBACK_TIMEZONES
  return Array.from(new Set([timezone.value, ...supported, ...FALLBACK_TIMEZONES].filter(Boolean))).sort()
})

const firstValue = (...values) => values.find((value) => String(value ?? '').trim() !== '')
const currentUserId = computed(() => String(session.userId || session.user?.user_id || session.user?.id || '').trim())

const formatOffset = (minutes) => {
  const sign = minutes >= 0 ? '+' : '-'
  const absoluteMinutes = Math.abs(minutes)
  const hours = Math.floor(absoluteMinutes / 60)
  const mins = String(absoluteMinutes % 60).padStart(2, '0')
  return `GMT${sign}${hours}:${mins}`
}

const offsetFromParts = (timeZone) => {
  const date = new Date()
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23'
  }).formatToParts(date)
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  const zonedUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  )
  return Math.round((zonedUtc - date.getTime()) / 60000)
}

const zoneForTimezone = (timeZone) => {
  try {
    const shortOffset = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset'
    }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value

    if (/^GMT[+-]\d{1,2}(:\d{2})?$/.test(shortOffset || '')) {
      return shortOffset.includes(':') ? shortOffset : `${shortOffset}:00`
    }

    if (shortOffset === 'GMT') return 'GMT+0:00'
    return formatOffset(offsetFromParts(timeZone))
  } catch {
    return formatOffset(-new Date().getTimezoneOffset())
  }
}

const zone = computed(() => zoneForTimezone(timezone.value))
const messageClass = computed(() => messageType.value === 'error'
  ? 'border-red-100 bg-red-50 text-red-700'
  : 'border-emerald-100 bg-emerald-50 text-emerald-700'
)

const loadTimezone = async () => {
  if (!session.user) session.loadUser()
  const profile = session.profile || await session.fetchProfile()
  timezone.value = String(firstValue(profile?.timezone, session.user?.timezone, timezone.value)).trim()
}

const saveTimezone = async () => {
  if (!currentUserId.value || saving.value) return

  saving.value = true
  message.value = ''

  try {
    await api.updateTimezone({
      id: currentUserId.value,
      timezone: timezone.value,
      zone: zone.value
    })

    const updatedTimezone = { timezone: timezone.value, zone: zone.value }
    session.updateProfileLocally(updatedTimezone)
    if (session.user) session.setUser({ ...session.user, ...updatedTimezone })
    messageType.value = 'success'
    message.value = 'Timezone updated.'
  } catch (error) {
    messageType.value = 'error'
    message.value = error?.response?.data?.message || 'Timezone could not be updated. Please try again.'
  } finally {
    saving.value = false
  }
}

onMounted(loadTimezone)
</script>
